import { NAME_TOOLTIP_TEXT, useAppForm } from '@massilia/features'
import { useCreateUserMutation } from '@massilia/queries'
import { Button } from '@massilia/ui'
import { revalidateLogic, standardSchemaValidators } from '@tanstack/react-form'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { z } from 'zod/v4'

import { AuthErrorComponent } from '../../components/login/auth-error-component'
import { resolveAuthErrorMessage } from '../../lib/auth-error-message'
import { registerSchema } from '../../lib/schemas'

export const Route = createFileRoute('/_auth/register')({
  component: RegisterComponent,
  validateSearch: z.object({
    email: z.email().optional(),
    firstName: z.string().optional(),
    lastName: z.string().optional(),
  }),
})

function RegisterComponent() {
  const { email, firstName, lastName } = Route.useSearch()
  const navigate = useNavigate()
  const [submitError, setSubmitError] = useState<null | string>(null)
  const { mutateAsync: createUserMutation } = useCreateUserMutation()

  const form = useAppForm({
    defaultValues: {
      email: email ?? '',
      firstName: firstName ?? '',
      lastName: lastName ?? '',
      hasAcceptedTerms: false,
      password: '',
    },
    listeners: {
      onChange: () => {
        if (submitError) {
          setSubmitError(null)
        }
      },
    },
    onSubmit: async ({ value }) => {
      try {
        await createUserMutation({
          email: value.email,
          firstName: value.firstName,
          lastName: value.lastName,
          password: value.password,
        })
        await navigate({ to: '/login' })
      } catch (error) {
        setSubmitError(resolveAuthErrorMessage(error))
      }
    },
    validationLogic: revalidateLogic({
      mode: 'submit',
      modeAfterSubmission: 'blur',
    }),
    validators: {
      onDynamic: ({ value }) =>
        standardSchemaValidators.validate({ validationSource: 'form', value }, registerSchema),
    },
  })

  return (
    <>
      <h1 className="text-gray-light-900 text-center text-xl font-semibold">
        Créez votre compte <br /> sur Massilia Voyages
      </h1>
      <form
        className="flex w-full flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
          void form.handleSubmit()
        }}
      >
        <form.AppField name="firstName">
          {(field) => (
            <field.TextField
              label="Prénom"
              placeholder="Prénom"
              questionToolTip={NAME_TOOLTIP_TEXT}
            />
          )}
        </form.AppField>
        <form.AppField name="lastName">
          {(field) => (
            <field.TextField
              label="Nom"
              placeholder="Nom"
              questionToolTip={NAME_TOOLTIP_TEXT}
            />
          )}
        </form.AppField>
        <form.AppField name="email">
          {(field) => <field.TextField label="Email" placeholder="email@example.com" />}
        </form.AppField>
        <form.AppField name="password">
          {(field) => <field.PasswordField label="Mot de passe" placeholder="Mot de passe" />}
        </form.AppField>
        <form.AppField name="hasAcceptedTerms">
          {(field) => (
            <field.CheckboxField label="J'accepte les conditions d'utilisation et la politique de confidentialité" />
          )}
        </form.AppField>
        {submitError && <AuthErrorComponent message={submitError} />}
        <form.Subscribe selector={(state) => state.isSubmitted && !state.isValid}>
          {(hasValidationErrors) =>
            hasValidationErrors ? (
              <AuthErrorComponent message="Veuillez compléter tous les champs." />
            ) : null
          }
        </form.Subscribe>
        <form.Subscribe
          selector={(state) => ({ isSubmitting: state.isSubmitting, values: state.values })}
        >
          {({ isSubmitting, values }) => (
            <Button
              className="bg-brand-600 hover:bg-brand-800 w-full px-8 py-2 text-white"
              disabled={isSubmitting || !values.hasAcceptedTerms}
              type="submit"
            >
              {isSubmitting ? 'Création du compte...' : 'Créer un compte'}
            </Button>
          )}
        </form.Subscribe>
        <Link to="/login">
          <Button className="text-brand-700 w-full hover:underline" variant="none">
            J&apos;ai déjà un compte
          </Button>
        </Link>
      </form>
    </>
  )
}
